import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const galleryQueues = new WeakMap();

function registerGalleryPreload(gallery, entry) {
  let queue = galleryQueues.get(gallery);
  if (!queue) {
    const controller = new AbortController();
    const { signal } = controller;
    queue = { entries: new Set(), done: new Set(), ready: false, busy: false, controller, timer: 0 };
    galleryQueues.set(gallery, queue);
    const run = async () => {
      if (!queue.ready || queue.busy || signal.aborted || document.hidden) return;
      const entries = [...queue.entries];
      const slides = [...gallery.querySelectorAll('slideshow-slide')];
      const current = slides.findIndex(slide => slide.getAttribute('aria-hidden') === 'false');
      entries.sort((a, b) => {
        const rank = item => (slides.indexOf(item.slide) - Math.max(0, current) + slides.length) % slides.length;
        return rank(a) - rank(b);
      });
      const next = entries.slice(0, 3).find(item => !queue.done.has(item) && item.allowed());
      if (!next) return;
      queue.busy = true;
      queue.done.add(next);
      const video = next.prepare();
      if (video) {
        await new Promise(resolve => {
          let timer;
          const events = ['progress', 'canplaythrough', 'suspend', 'error'];
          const finish = () => {
            clearTimeout(timer);
            events.forEach(name => video.removeEventListener(name, check));
            signal.removeEventListener('abort', finish);
            resolve();
          };
          const check = event => {
            const end = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
            if (event?.type === 'error' || event?.type === 'suspend' ||
              (video.readyState >= 3 && end >= Math.min(video.duration || 3, 3) - 0.1)) finish();
          };
          events.forEach(name => video.addEventListener(name, check));
          signal.addEventListener('abort', finish, { once: true });
          timer = setTimeout(finish, 6000);
          check();
        });
      }
      queue.busy = false;
      if (!signal.aborted) queue.timer = setTimeout(run, 120);
    };
    queue.run = run;
    const start = () => {
      if (signal.aborted || queue.ready) return;
      const hero = gallery.querySelector('slideshow-slide .product-media__image');
      if (hero && !hero.complete) {
        hero.addEventListener('load', start, { once: true, signal });
        hero.addEventListener('error', start, { once: true, signal });
        return;
      }
      queue.ready = true;
      const idle = () => { if (!signal.aborted) run(); };
      if ('requestIdleCallback' in window) window.requestIdleCallback(idle, { timeout: 250 });
      else queue.timer = setTimeout(idle, 50);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal });
    else queue.timer = setTimeout(start, 0);
    gallery.addEventListener('slideshow:select', run, { signal });
    document.addEventListener('visibilitychange', run, { signal });
  }
  queue.entries.add(entry);
  return () => {
    queue.entries.delete(entry);
    queue.done.delete(entry);
    if (!queue.entries.size) {
      queue.controller.abort();
      clearTimeout(queue.timer);
      galleryQueues.delete(gallery);
    }
  };
}

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, event => {
      if (event.detail?.resource !== this) this.pauseMedia();
    }, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    this.#setupGalleryAutoplay(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#galleryIntersection?.disconnect();
    this.#gallerySelection?.disconnect();
    this.#galleryRelease?.();
    if (this.#gallerySlide) {
      this.pauseMedia();
      this.#restoreGalleryPoster();
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    if (this.#gallerySlide) {
      this.#playGalleryVideo(true);
      return;
    }
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.#gallerySlide) {
      if (this.isPlaying || this.#galleryPending) {
        this.#galleryUserPaused = true;
        this.pauseMedia();
      } else {
        this.#playGalleryVideo(true);
      }
      return;
    }
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    if (this.#gallerySlide) {
      this.#playGalleryVideo(true);
      return;
    }
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    if (this.#gallerySlide) {
      this.#galleryWanted = false;
      clearTimeout(this.#galleryRetry);
      this.#galleryToken++;
      this.#galleryPending = false;
    }
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
  #gallerySlide = null;
  #galleryIntersection = null;
  #gallerySelection = null;
  #galleryMotion = null;
  #galleryRelease = null;
  #galleryUserPaused = false;
  #galleryManual = false;
  #galleryBlocked = false;
  #galleryPending = false;
  #galleryWanted = false;
  #galleryToken = 0;
  #galleryRetry = 0;
  #galleryRetryCount = 0;
  #galleryBoundVideo = null;

  #setupGalleryAutoplay(signal) {
    const gallery = this.closest('media-gallery');
    if (!this.hasAttribute('data-ct-gallery-autoplay') || !gallery) return;
    this.#gallerySlide = this.closest('slideshow-slide');
    if (!this.#gallerySlide) return;
    this.#galleryMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#restoreGalleryPoster();
    const sync = () => { if (!signal.aborted) this.#syncGalleryVideo(); };
    let scheduled = false;
    const afterLayout = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        sync();
      });
    };
    this.#galleryIntersection = new IntersectionObserver(sync, { threshold: [0, 0.5, 1] });
    this.#galleryIntersection.observe(this);
    this.#gallerySelection = new MutationObserver(afterLayout);
    this.#gallerySelection.observe(this.#gallerySlide, { attributes: true, attributeFilter: ['aria-hidden'] });
    gallery.addEventListener('slideshow:select', afterLayout, { signal });
    gallery.addEventListener('scroll', afterLayout, { capture: true, passive: true, signal });
    gallery.addEventListener('scrollend', sync, { capture: true, passive: true, signal });
    const gesture = () => {
      if (!this.#galleryUserPaused) this.#galleryBlocked = false;
      sync();
      afterLayout();
    };
    gallery.addEventListener('touchend', gesture, { passive: true, signal });
    gallery.addEventListener('pointerup', gesture, { passive: true, signal });
    document.addEventListener('visibilitychange', sync, { signal });
    window.addEventListener('pageshow', sync, { signal });
    this.#galleryMotion.addEventListener('change', () => {
      this.#galleryManual = false;
      sync();
    }, { signal });
    const video = this.querySelector('video');
    if (video) this.#bindGalleryVideo(video, signal);
    this.#galleryRelease = registerGalleryPreload(gallery, {
      slide: this.#gallerySlide,
      allowed: () => this.isConnected && !this.#galleryMotion.matches &&
        !navigator.connection?.saveData && !['slow-2g', '2g'].includes(navigator.connection?.effectiveType),
      prepare: () => this.#prepareGalleryVideo()
    });
    afterLayout();
  }

  #galleryIsActive() {
    if (!this.isConnected || document.hidden || !this.#gallerySlide) return false;
    const rect = this.getBoundingClientRect();
    const viewport = this.closest('slideshow-slides')?.getBoundingClientRect();
    if (!viewport || rect.width <= 0 || rect.height <= 0) return false;
    const width = Math.max(0, Math.min(rect.right, viewport.right, window.innerWidth) -
      Math.max(rect.left, viewport.left, 0));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return width > rect.width * 0.5 && height >= Math.min(rect.height, window.innerHeight) * 0.5;
  }

  #syncGalleryVideo() {
    if (!this.#galleryIsActive()) {
      this.#galleryManual = false;
      this.#galleryBlocked = false;
      this.#galleryRetryCount = 0;
      this.pauseMedia();
      this.#restoreGalleryPoster();
      return;
    }
    if (this.#galleryUserPaused || this.#galleryBlocked ||
      (this.#galleryMotion.matches && !this.#galleryManual)) {
      this.pauseMedia();
      return;
    }
    this.#playGalleryVideo();
  }

  #prepareGalleryVideo() {
    let video = this.querySelector('video');
    if (video) return video;
    video = this.querySelector('template')?.content.querySelector('video')?.cloneNode(true);
    if (!(video instanceof HTMLVideoElement)) return null;
    const poster = this.closest('.product-media')?.querySelector('.product-media__image');
    if (poster) {
      poster.loading = 'eager';
      video.poster = poster.currentSrc || poster.src;
    }
    this.#bindGalleryVideo(video, this.#abortController.signal);
    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(video);
    video.load();
    return video;
  }

  #showGalleryFrame(video) {
    if (this.hasAttribute('data-ct-frame-ready') || !this.#galleryIsActive() || video.paused || video.readyState < 2) return;
    this.setAttribute('data-ct-frame-ready', '');
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    this.refs.deferredMediaPlayButton?.setAttribute('aria-hidden', 'true');
    if (this.refs.deferredMediaPlayButton) this.refs.deferredMediaPlayButton.tabIndex = -1;
  }

  #restoreGalleryPoster() {
    this.removeAttribute('data-ct-frame-ready');
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.refs.deferredMediaPlayButton?.removeAttribute('aria-hidden');
    if (this.refs.deferredMediaPlayButton) this.refs.deferredMediaPlayButton.tabIndex = 0;
  }

  #retryGalleryVideo() {
    if (!this.#galleryWanted || !this.#galleryIsActive() || this.#galleryUserPaused ||
      this.#galleryBlocked || this.#galleryRetryCount >= 3) return;
    clearTimeout(this.#galleryRetry);
    this.#galleryRetryCount++;
    this.#galleryRetry = setTimeout(() => {
      this.#galleryPending = false;
      this.#playGalleryVideo();
    }, 150 * this.#galleryRetryCount);
  }

  #bindGalleryVideo(video, signal) {
    if (this.#galleryBoundVideo === video) return;
    this.#galleryBoundVideo = video;
    signal.addEventListener('abort', () => { this.#galleryBoundVideo = null; }, { once: true });
    video.removeAttribute('autoplay');
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.preload = 'auto';
    video.addEventListener('play', () => {
      if (!this.#galleryIsActive() || this.#galleryUserPaused) this.pauseMedia();
    }, { signal });
    video.addEventListener('playing', () => {
      if (video.paused) return;
      if (!this.#galleryIsActive() || this.#galleryUserPaused) {
        this.pauseMedia();
        return;
      }
      this.#galleryRetryCount = 0;
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      this.#showGalleryFrame(video);
    }, { signal });
    video.addEventListener('timeupdate', () => this.#showGalleryFrame(video), { signal });
    const ready = () => {
      if (!this.#galleryRetryCount) this.#syncGalleryVideo();
    };
    video.addEventListener('canplay', ready, { signal });
    video.addEventListener('loadeddata', ready, { signal });
    video.addEventListener('pause', () => {
      if (!video.paused) return;
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
      if (!this.#galleryUserPaused) this.#restoreGalleryPoster();
      this.#retryGalleryVideo();
    }, { signal });
    video.addEventListener('emptied', () => this.#restoreGalleryPoster(), { signal });
    video.addEventListener('error', () => {
      this.#galleryBlocked = true;
      this.pauseMedia();
      this.#restoreGalleryPoster();
    }, { signal });
  }

  async #playGalleryVideo(manual = false) {
    if (manual) {
      this.#galleryUserPaused = false;
      this.#galleryBlocked = false;
      this.#galleryManual = true;
      this.#galleryRetryCount = 0;
    }
    if (!this.#galleryIsActive() || this.#galleryUserPaused || this.#galleryBlocked ||
      (this.#galleryMotion.matches && !this.#galleryManual)) return;
    let video = this.querySelector('video');
    if (this.#galleryPending || (video && !video.paused)) return;
    clearTimeout(this.#galleryRetry);
    video = this.#prepareGalleryVideo();
    if (!video) return;
    if (manual && video.error) video.load();
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    const token = ++this.#galleryToken;
    this.#galleryWanted = true;
    this.#galleryPending = true;
    this.updatePlayPauseHint(false);
    try {
      await video.play();
      if (token !== this.#galleryToken) return;
      if (!this.#galleryIsActive() || this.#galleryUserPaused) {
        this.pauseMedia();
        return;
      }
      this.isPlaying = !video.paused;
      this.updatePlayPauseHint(this.isPlaying);
      this.#showGalleryFrame(video);
    } catch (error) {
      if (token === this.#galleryToken) {
        this.#galleryPending = false;
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
        this.#restoreGalleryPoster();
        if (error.name === 'AbortError') this.#retryGalleryVideo();
        else this.#galleryBlocked = true;
      }
    } finally {
      if (token === this.#galleryToken) this.#galleryPending = false;
    }
  }

}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
